/**
 * İç içe kaydırılabilir alanlar için yardımcı fonksiyonlar
 * Bu script, Lenis smooth scroll sistemi için sayfa içindeki kaydırılabilir 
 * alanların düzgün çalışmasını sağlar.
 */

import { getLenis } from './smoothScroll';

/**
 * Kaydırılabilir elementleri otomatik tespit edip Lenis smooth scroll ile uyumlu hale getiren yardımcı fonksiyonlar
 */

/**
 * scrollHelper.js
 * Kaydırılabilir alanları otomatik olarak tespit eden ve Lenis ile uyumlu hale getiren yardımcı
 */

// Scrollun engellenmesi gereken seçiciler
const SCROLL_PREVENT_SELECTORS = [
  '.scrollable',
  '.overflow-auto',
  '.overflow-y-auto',
  '.overflow-scroll',
  '.overflow-y-scroll',
  '[data-scroll-container]',
  'textarea',
  'select'
];

// Touch scroll desteği için gerekli değişkenler
let touchStartY = 0;
let touchStartX = 0;

/**
 * Kaydırılabilir alanları tespit eden ana fonksiyon
 * @returns {Function} Cleanup fonksiyonu
 */
export const initScrollHelpers = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  
  // Sayfa yüklendiğinde mevcut kaydırılabilir elementleri tespit et
  detectScrollableElements();
  
  // DOM değişikliklerini izle ve yeni eklenen kaydırılabilir elementleri tespit et
  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        // Yeni eklenen nodelar için kaydırılabilir elementleri kontrol et
        setTimeout(() => detectScrollableElements(), 100);
        break;
      }
    }
  });
  
  // Tüm DOM ağacını izle
  observer.observe(document.body, { 
    childList: true, 
    subtree: true 
  });
  
  // Touch olayları için özel ele alıcılar ekle
  initTouchScrollHandling();
  
  // Wheel olayları için özel ele alıcı ekle
  document.addEventListener('wheel', handleNestedScroll, { passive: false });
  
  // Temizleme fonksiyonu
  return () => {
    observer.disconnect();
    document.removeEventListener('wheel', handleNestedScroll);
    document.removeEventListener('touchstart', handleTouchStart);
    document.removeEventListener('touchmove', handleTouchMove);
  };
};

/**
 * Kaydırılabilir tüm elementleri tespit et ve işaretle
 */
export const detectScrollableElements = () => {
  // Önceden tanımlanmış seçicilere uyan elementleri bul
  const preDefinedElements = document.querySelectorAll(SCROLL_PREVENT_SELECTORS.join(','));
  preDefinedElements.forEach(el => {
    if (!el.hasAttribute('data-lenis-prevent')) {
      el.setAttribute('data-lenis-prevent', '');
    }
  });
  
  // Diğer potansiyel olarak kaydırılabilir elementleri tespit et
  const allElements = document.querySelectorAll('*');
  allElements.forEach(el => {
    // Halihazırda işaretlenmiş elementleri atla
    if (el.hasAttribute('data-lenis-prevent')) return;
    
    // Eğer element HTML veya BODY ise atla
    if (el.tagName === 'HTML' || el.tagName === 'BODY') return;
    
    const style = window.getComputedStyle(el);
    const isScrollable = (
      (style.overflowY === 'auto' || style.overflowY === 'scroll') && 
      el.scrollHeight > el.clientHeight
    ) || (
      (style.overflowX === 'auto' || style.overflowX === 'scroll') && 
      el.scrollWidth > el.clientWidth
    );
    
    if (isScrollable) {
      el.setAttribute('data-lenis-prevent', '');
    }
  });
};

/**
 * İç içe scroll olaylarını ele al
 * @param {WheelEvent} e - Wheel olayı
 */
export const handleNestedScroll = (e) => {
  const scrollableParent = findScrollableParent(e.target);
  
  if (scrollableParent) {
    // Eğer element kaydırılabilir ve scroll sınırlarına ulaşmamışsa
    // olayı engelleme ve scroll işlemini yönet
    const { scrollTop, scrollHeight, clientHeight } = scrollableParent;
    const isAtTop = scrollTop <= 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
    
    // Scroll yönünü belirle
    const wheelDelta = e.deltaY;
    const scrollingDown = wheelDelta > 0;
    const scrollingUp = wheelDelta < 0;
    
    // Eğer scroll sınırına ulaşmışsa ve kullanıcı o yöne kaydırmaya çalışıyorsa
    // olayı propagasyona izin ver (Lenis'in işlemesine izin ver)
    if ((isAtTop && scrollingUp) || (isAtBottom && scrollingDown)) {
      return true;
    }
    
    // Aksi takdirde kaydırılabilir element kendi içinde kaydırılmalı
    // ve sayfa kaydırması engellenmeli
    // - e.stopPropagation() default browser davranışını etkilemez
    e.stopPropagation(); // Event'in üst elementlere yayılmasını engelle
    return true;
  }
  
  // Kaydırılabilir parent yoksa event'i normal şekilde işle
  return true;
};

/**
 * Touch scroll desteği için başlangıç ele alıcı
 */
export const initTouchScrollHandling = () => {
  document.addEventListener('touchstart', handleTouchStart, { passive: true });
  document.addEventListener('touchmove', handleTouchMove, { passive: false });
};

/**
 * Touch başlangıç pozisyonunu kaydet
 * @param {TouchEvent} e - Touch olayı
 */
const handleTouchStart = (e) => {
  touchStartY = e.touches[0].clientY;
  touchStartX = e.touches[0].clientX;
};

/**
 * Touch hareketi sırasında scroll davranışını yönet
 * @param {TouchEvent} e - Touch olayı
 */
const handleTouchMove = (e) => {
  if (e.touches.length !== 1) return;
  
  const touchY = e.touches[0].clientY;
  const touchX = e.touches[0].clientX;
  const deltaY = touchStartY - touchY;
  const deltaX = touchStartX - touchX;
  
  // Dikey kaydırma daha baskınsa
  if (Math.abs(deltaY) > Math.abs(deltaX)) {
    const scrollableParent = findScrollableParent(e.target);
    
    if (scrollableParent) {
      const { scrollTop, scrollHeight, clientHeight } = scrollableParent;
      const isAtTop = scrollTop <= 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
      
      // Kaydırma yönünü belirle
      const scrollingDown = deltaY > 0;
      const scrollingUp = deltaY < 0;
      
      // Eğer scroll sınırına ulaşmışsa ve kullanıcı o yöne kaydırmaya çalışıyorsa
      // olayı propagasyona izin ver
      if ((isAtTop && scrollingUp) || (isAtBottom && scrollingDown)) {
        return true;
      }
      
      // Aksi takdirde sayfa kaydırması engellenmeli
      e.stopPropagation();
    }
  }
};

/**
 * En yakın kaydırılabilir parent elementi bul
 * @param {HTMLElement} element - Başlangıç elementi
 * @returns {HTMLElement|null} Kaydırılabilir parent element veya null
 */
export const findScrollableParent = (element) => {
  if (!element || element === document.body || element === document.documentElement) {
    return null;
  }
  
  // Element data-lenis-prevent özelliğine sahipse, kaydırılabilir olarak kabul et
  if (element.hasAttribute('data-lenis-prevent')) {
    const style = window.getComputedStyle(element);
    
    // Dikey kaydırma için
    if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && 
        element.scrollHeight > element.clientHeight) {
      return element;
    }
    
    // Yatay kaydırma için
    if ((style.overflowX === 'auto' || style.overflowX === 'scroll') && 
        element.scrollWidth > element.clientWidth) {
      return element;
    }
  }
  
  // Parent elementler için recursive olarak kontrol et
  return findScrollableParent(element.parentElement);
};

/**
 * Bir elementin belirli bir yönde kaydırılabilir olup olmadığını kontrol et
 * @param {HTMLElement} element - Kontrol edilecek element
 * @param {string} direction - Yön ('vertical' veya 'horizontal')
 * @returns {boolean} Kaydırılabilir olup olmadığı
 */
export const isElementScrollable = (element, direction = 'vertical') => {
  if (!element) return false;
  
  const style = window.getComputedStyle(element);
  
  if (direction === 'vertical') {
    return (
      (style.overflowY === 'auto' || style.overflowY === 'scroll') && 
      element.scrollHeight > element.clientHeight
    );
  } else {
    return (
      (style.overflowX === 'auto' || style.overflowX === 'scroll') && 
      element.scrollWidth > element.clientWidth
    );
  }
};

export default {
  initScrollHelpers,
  detectScrollableElements,
  findScrollableParent,
  isElementScrollable
}; 